import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Table,
  TableBody,
  AppBar,
  Toolbar,
  Typography,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Box,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LoadingBar from "react-top-loading-bar";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import base64 from "base64-js";
import "jspdf-autotable";     
import { jsPDF } from "jspdf";
import PrintIcon from '@mui/icons-material/Print';

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const StockReturn = () => {
  const [showUserGuide, setShowUserGuide] = useState(false);
  const navigate = useNavigate();
  const { encodedFormId } = useParams();
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [stockInwardID, setStockInwardID] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [processingPrint, setProcessingPrint] = useState(false);

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
        setCanEdit(responseData[0].uModify === 1);
        setCanDelete(responseData[0].uDelete === 1);
      } else {
        console.error("Authorization failed:", response.status);
        setSnackbarMessage("Invalid authorization response.");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage("An error occurred during authorization.");
      setSnackbarOpen(true);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/StockTransaction/ListStockReturns`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      if (response.ok) {
        const responseData = await response.json();
        setCategories(responseData || []);
      } else {
        console.error("Failed to fetch categories:", response.status);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const fetchStockInward = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const searchQuery = searchTerm ? `?searchTerm=${searchTerm}` : "";
      const response = await fetch(`${apiUrl}/Inventory/StockTransaction/ListStockReturns${searchQuery}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (response.ok) {
        const responseData = await response.json();
        setCategories(responseData || []);
      } else {
        console.error("Failed to fetch stock inward data:", response.status);
      }
    } catch (error) {
      console.error("Error fetching stock inward data:", error);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

 const printReceipt = async (id) => {
    if (!id) {
      setSnackbarMessage("No stock return ID available for printing");
      setSnackbarOpen(true);
      return;
    }
    setStockInwardID(id);
    setProcessingPrint(true);
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/Inventory/StockTransaction/GetStockReturnDetails/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const { header, details } = result;

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const maxWidth = pageWidth - 2 * margin;

      // Logo: hardcoded fallback drawn first, then an attempt to fetch and
      // validate the real school/branch logo — same technique as AdmReport.
      await new Promise((resolveAll) => {
        (async () => {
          let logoDataUrl = null;
          try {
            const hardcodedLogo =
              "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=";
            logoDataUrl = hardcodedLogo;

            const img = new Image();
            const loadPromise = new Promise((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => reject(new Error("Failed to load hardcoded logo image"));
              img.src = logoDataUrl;
            });
            await loadPromise;

            doc.setFillColor(240, 240, 240);
            doc.rect(margin - 2, 5, 34, 34, "F");
            doc.setFillColor(255, 255, 255);
            doc.rect(margin, 7, 30, 30, "F");
            doc.addImage(logoDataUrl, "PNG", margin + 2, 9, 26, 26);

            if (!token) {
              throw new Error("No authentication token found");
            }

            const logoResponse = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
              body: JSON.stringify({}),
            });

            if (!logoResponse.ok) {
              throw new Error(`API error: ${logoResponse.status} ${logoResponse.statusText}`);
            }

            const apiData = await logoResponse.json();
            const logoImg = apiData?.[0]?.logoImg;
            if (!logoImg || typeof logoImg !== "string" || logoImg.trim() === "") {
              throw new Error("Invalid or missing logoImg in API response");
            }

            let cleanedBase64 = logoImg
              .replace(/^data:image\/[a-zA-Z]+;base64,/, "")
              .replace(/\s/g, "")
              .replace(/\n/g, "");
            const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
            cleanedBase64 += "=".repeat(paddingLength);

            const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
            if (!isValidBase64) {
              throw new Error("Invalid base64 string for logoImg");
            }

            logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

            const apiImg = new Image();
            const apiLoadPromise = new Promise((resolve, reject) => {
              apiImg.onload = () => resolve();
              apiImg.onerror = () => reject(new Error("Failed to load API logo image"));
              apiImg.src = logoDataUrl;
            });
            await apiLoadPromise;

            doc.setFillColor(240, 240, 240);
            doc.rect(margin - 2, 5, 34, 34, "F");
            doc.setFillColor(255, 255, 255);
            doc.rect(margin, 7, 30, 30, "F");
            doc.addImage(logoDataUrl, "PNG", margin + 2, 9, 26, 26);
          } catch (error) {
            console.error("Error fetching or processing logo:", error.message);
            doc.setFont("times", "bold");
            doc.setFontSize(10);
            doc.setTextColor(200, 0, 0);
            doc.text("Logo Failed", margin, 50);
            doc.setTextColor(0, 0, 0);
          }

          // Header — same fonts/colors as AdmReport
          doc.setFont("times", "bold");
          doc.setFontSize(18);
          doc.setTextColor(0, 51, 102);
          doc.text(header.branchName || "", pageWidth / 2, 14, { align: "center" });
          doc.setLineWidth(0.4);
          doc.setDrawColor(0, 51, 102);
          doc.line(margin + 40, 18, pageWidth - margin - 40, 18);
          doc.setFontSize(16);
          doc.text("Stock Return Receipt", pageWidth / 2, 24, { align: "center" });
          doc.setLineWidth(0.2);
          doc.setDrawColor(200, 200, 200);
          doc.line(margin + 65, 27, pageWidth - margin - 65, 27);
          doc.setFontSize(12);
          doc.setFont("times", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(`Return Code: ${header.returnCode || ""}`, pageWidth / 2, 35, { align: "center" });
          doc.text(`Return Date: ${header.returnDate || ""}`, pageWidth / 2, 41, { align: "center" });

          // Details box — text wrapped per column and box height computed
          // dynamically, so long values (branch names, etc.) never overlap.
          const leftColX = margin + 5;
          const rightColX = pageWidth / 2 + 5;
          const colWidth = pageWidth / 2 - margin - 10;

          doc.setFont("times", "normal");
          doc.setFontSize(10);

          const originalIssuanceLines = doc.splitTextToSize(
            `Original Issuance: ${header.issuanceCode || "N/A"}`,
            colWidth
          );
          const returnedByLines = doc.splitTextToSize(
            `Returned By: ${header.returnedByName || "N/A"} (${header.returnedByType || "N/A"})`,
            colWidth
          );
          const branchLines = doc.splitTextToSize(
            `Branch: ${header.branchName || "N/A"} (ID: ${header.branchID ?? "N/A"})`,
            colWidth
          );
          const createdByLines = doc.splitTextToSize(
            `Created By: ${header.createdByName || "N/A"}`,
            colWidth
          );
          const createdDateLines = doc.splitTextToSize(
            `Created Date: ${header.createdDate || "N/A"}`,
            colWidth
          );
          const totalQtyLines = doc.splitTextToSize(
            `Total Quantity: ${header.totalQuantity ?? 0}`,
            colWidth
          );

          const lineHeight = 5;
          const leftColLineCount = originalIssuanceLines.length + returnedByLines.length;
          const rightColLineCount = branchLines.length + createdByLines.length + createdDateLines.length + totalQtyLines.length;
          const maxLineCount = Math.max(leftColLineCount, rightColLineCount);

          const boxTopY = 48;
          const boxHeaderHeight = 6;
          const boxHeight = boxHeaderHeight + maxLineCount * lineHeight + 6;

          doc.setFillColor(245, 245, 245);
          doc.rect(margin, boxTopY, maxWidth, boxHeight, "F");

          doc.setFont("times", "bold");
          doc.setFontSize(10);
          doc.setTextColor(0, 51, 102);
          doc.text("Return Details:", leftColX, boxTopY + 6);

          doc.setFont("times", "normal");
          doc.setTextColor(0, 0, 0);

          let leftY = boxTopY + 6 + lineHeight;
          [...originalIssuanceLines, ...returnedByLines].forEach((line) => {
            doc.text(line, leftColX, leftY);
            leftY += lineHeight;
          });

          let rightY = boxTopY + 6 + lineHeight;
          [...branchLines, ...createdByLines, ...createdDateLines, ...totalQtyLines].forEach((line) => {
            doc.text(line, rightColX, rightY);
            rightY += lineHeight;
          });

          const tableStartY = boxTopY + boxHeight + 8;

          // Table — same head/alternate row colors and font as AdmReport
          const tableData = (details || []).map((item, index) => [
            (index + 1).toString(),
            item.itemCode || "N/A",
            item.itemName || "N/A",
            item.quantityReturned ?? 0,
          ]);

          doc.autoTable({
            head: [["S.No", "Item Code", "Item Name", "Quantity Returned"]],
            body: tableData,
            startY: tableStartY,
            margin: { left: margin, right: margin },
            styles: {
              font: "times",
              fontSize: 10,
              cellPadding: 3,
              overflow: "linebreak",
              minCellHeight: 8,
              textColor: [33, 33, 33],
            },
            headStyles: {
              fillColor: [0, 51, 102],
              textColor: [255, 255, 255],
              fontSize: 10,
              fontStyle: "bold",
            },
            alternateRowStyles: { fillColor: [230, 240, 255] },
            columnStyles: {
              0: { cellWidth: 15 },
              1: { cellWidth: 45 },
              2: { cellWidth: 70 },
              3: { cellWidth: 40, halign: "right" },
            },
          });

          // Signature footer
          const afterTableY = doc.lastAutoTable.finalY + 20;
          doc.setFont("times", "normal");
          doc.setFontSize(10);
          doc.setDrawColor(150, 150, 150);

          doc.line(margin, afterTableY, margin + 60, afterTableY);
          doc.text("Returned By", margin, afterTableY + 5);
          doc.setFontSize(9);
          doc.setTextColor(120, 120, 120);
          doc.text(header.returnedByName || "Student/Staff", margin, afterTableY + 10);

          doc.setFont("times", "normal");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.line(pageWidth - margin - 60, afterTableY, pageWidth - margin, afterTableY);
          doc.text("Received By", pageWidth - margin - 60, afterTableY + 5);
          doc.setFontSize(9);
          doc.setTextColor(120, 120, 120);
          doc.text(header.createdByName || "Staff", pageWidth - margin - 60, afterTableY + 10);

          resolveAll();
        })();
      });

      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pdfWindow = window.open(pdfUrl, "_blank");
      if (pdfWindow) {
        let printed = false;
        const triggerPrint = () => {
          if (!printed) {
            printed = true;
            pdfWindow.print();
          }
        };
        pdfWindow.onload = triggerPrint;
        setTimeout(triggerPrint, 1000);
      } else {
        setSnackbarMessage("Please allow popups to view the PDF receipt");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error in printReceipt:", error);
      setSnackbarMessage("Failed to generate stock return receipt");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setProcessingPrint(false);
    }
  };

  const downloadInvoiceCopy = async (id) => {
    if (!id) {
      setSnackbarMessage("No stock inward ID available for invoice");
      setSnackbarOpen(true);
      return;
    }
    try {
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/Inventory/InwardItem/GetStockInwardDetails/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const { header } = result;
      if (header.invoiceCopy) {
        let fileData, fileType, fileName;
        if (header.invoiceCopy.startsWith('data:')) {
          const matches = header.invoiceCopy.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            fileType = matches[1];
            fileData = atob(matches[2]);
          } else {
            throw new Error("Invalid base64 format");
          }
        } else {
          fileData = atob(header.invoiceCopy);
          const uint8Array = new Uint8Array(fileData.length);
          for (let i = 0; i < fileData.length; i++) {
            uint8Array[i] = fileData.charCodeAt(i);
          }
          const headerBytes = uint8Array.slice(0, 4);
          if (headerBytes[0] === 0x25 && headerBytes[1] === 0x50 && headerBytes[2] === 0x44 && headerBytes[3] === 0x46) {
            fileType = "application/pdf";
            fileName = `invoice_${header.receiptCode || id}.pdf`;
          } else if (headerBytes[0] === 0xFF && headerBytes[1] === 0xD8) {
            fileType = "image/jpeg";
            fileName = `invoice_${header.receiptCode || id}.jpg`;
          } else if (headerBytes[0] === 0x89 && headerBytes[1] === 0x50 && headerBytes[2] === 0x4E && headerBytes[3] === 0x47) {
            fileType = "image/png";
            fileName = `invoice_${header.receiptCode || id}.png`;
          } else {
            fileType = "application/pdf";
            fileName = `invoice_${header.receiptCode || id}.pdf`;
          }
        }
        const byteCharacters = fileData;
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: fileType });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName || `invoice_${header.receiptCode || id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbarMessage("Invoice downloaded successfully");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("No invoice copy available for this stock inward");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setSnackbarMessage("Failed to download invoice copy");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = () => {
    if (authorization?.uWrite !== 1) {
      setSnackbarMessage("You don't have permission to add stock returns.");
      setSnackbarOpen(true);
      return;
    }
    navigate("/stockreturn/add", { state: { encodedFormId } });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    fetchStockInward();
    Authorizer();
  }, []);

  const filteredCategories = categories.filter((category) =>
    category.returnCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.returnedByName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  return (
    <div className="container">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{fontWeight:'bold'}}>
            Stock Return <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
          </Typography>
          <Dialog
            open={showUserGuide}
            onClose={() => setShowUserGuide(false)}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                py: 2,
              }}
            >
              Stock Return User Guide
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1898c2",
                  fontWeight: "600",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                Quick Steps to Use Stock Return
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: "none",
                  padding: 0,
                  "& li": {
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    fontSize: "0.9rem",
                    color: "#333",
                  },
                  "& li::before": {
                    content: '"\\2022"',
                    color: "#1898c2",
                    fontWeight: "bold",
                    display: "inline-block",
                    width: "1.5em",
                    mr: 1,
                    fontSize: "1.2rem",
                  },
                  "& strong": {
                    margin: "0 0.2em",
                  },
                }}
              >
                <li><strong>Search</strong> returns by code or recipient name</li>
                <li><strong>Initiate</strong> new returns with "Return Stock" button</li>
                <li><strong>View</strong> return details in the table</li>
                <li><strong>Print</strong> receipts for any return record</li>
                <li>Use <strong>pagination</strong> to navigate large lists</li>
                <li><strong>Track</strong> total quantities returned</li>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <StyledButton
                onClick={() => setShowUserGuide(false)}
                color="primary"
                variant="contained"
                sx={{
                  backgroundColor: "#1898c2",
                  "&:hover": { backgroundColor: "#156f9a" },
                  borderRadius: "8px",
                  px: 4,
                }}
              >
                Close
              </StyledButton>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: 16, marginTop: 6 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search Stock..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, backgroundColor: "#f9f9f9" }}
          />
          <Tooltip title={authorization?.uWrite === 1 ? "" : "Not authorized to return stock"}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddCategory}
                style={{ height: "50px"}}
                disabled={authorization?.uWrite !== 1}
              >
                Return Stock
              </Button>
            </span>
          </Tooltip>
        </div>
        {isLoading ? (
          <Typography>Loading Stock...</Typography>
        ) : (
          <>
           <TableContainer component={Paper} style={{ marginTop: 16 }}>
  <Table stickyHeader sx={{
    minWidth: 650,
    border: '1px solid #ccc',
    '& .MuiTableCell-root': {
      borderRight: '1px solid #ccc',
      fontSize: '1rem',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
    },
  }}>
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          Return Code
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          Name
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "15%" }}>
          Total Quantity
        </TableCell>
        <TableCell sx={{textAlign:'center', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          View Receipt
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', borderRight: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          Return Date
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {paginatedCategories.length > 0 ? (
        paginatedCategories.map((category) => (
          <TableRow key={category.stockReturnID} sx={{ borderTop: '1px solid #ccc' }}>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', whiteSpace: "normal", wordWrap: "break-word" }}>
              {category.returnCode || "N/A"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', whiteSpace: "normal", wordWrap: "break-word" }}>
              {`${category.returnedByName} (${category.returnedByType})` || "N/A"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
              {category.totalQuantity || "N/A"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', textAlign: 'center' }}>
              <Tooltip title={authorization?.uPrint === 1 ? "" : "Not authorized to print receipt"}arrow>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => printReceipt(category.stockReturnID)}
                    disabled={processingPrint && stockInwardID === category.stockReturnID || authorization?.uPrint !== 1}
                    sx={{ borderRadius: "4px", backgroundColor: "#986AB6" }}
                  >
                    {processingPrint && stockInwardID === category.stockReturnID ? "Processing..." : <PrintIcon />}
                  </Button>
                </span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ fontSize: '1.1rem' }}>
              {category.returnDate || "N/A"}
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={5} sx={{ textAlign: 'center' }}>No stock inward data available</TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCategories.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StockReturn;
